import { FC, useState } from "react";
import { useSnackbar } from "notistack";
import { v7 as uuidv7 } from "uuid";

import { FormWrapper } from "@/components/Forms/components/MonolithFormComponents";
import { usePermissions } from "@/hooks/usePermissions";
import { hasDataArray } from "@/api/connection.types";
import { AssetAttachmentsAPI } from "@/Features/AssetAttachments/asset-attachments.api";
import { Uploader } from "@/Features/Files/Uploader";
import { FileStatus } from "@/Features/Files/files.types";
import { useCreateAssetValidation } from "@/Features/AssetValidations/asset-validations.query";
import type { AssetValidation } from "@/Features/AssetValidations/asset-validations.types";
import DefaultValidationForm from "./DefaultValidationForm";
import {
  ValidationFormData,
  toCreateValidationBody,
} from "./validationForm.utils";

interface CreateValidationFormProps {
  assetUuid: string;
  width?: number | undefined;
  onSubmit: () => void;
  onCancel: () => void;
  onSubmittingChange?: (submitting: boolean) => void;
}

// Part PUTs go straight to storage, so a few files in parallel is fine; three
// keeps a 20-file drop from opening 20 uploads at once.
const UPLOAD_CONCURRENCY = 3;

// One document as a report of the validation (ENG-2291): start, part PUTs and
// complete through the shared Uploader, headless (no FileUploadManager row),
// since the form shows its own failed list. A failed upload deletes its
// pending row (best effort, silent), so the retry doesn't collide with it and
// get renamed. The row is read first: a complete that went through with only
// its response lost leaves it active, which is a success, and a failed read
// deletes nothing.
const uploadReport = (
  file: File,
  asset_uuid: string,
  validation_uuid: string
) =>
  new Promise<void>((resolve, reject) => {
    const uuid = uuidv7();
    new Uploader({
      file,
      // The asset routes store only the hashes the client sends.
      hashFile: true,
      metadata: {
        uuid,
        name: file.name,
        size: file.size,
        client_modified: new Date(file.lastModified).toISOString(),
      },
      // The form reports failures in one snackbar of its own.
      api: AssetAttachmentsAPI.uploaderApi(asset_uuid, {
        validation_uuid,
        skipErrorToast: true,
      }),
      onComplete: () => resolve(),
      onError: (error) =>
        AssetAttachmentsAPI.get(asset_uuid, { uuid }).then(
          async (res) => {
            const row = hasDataArray(res) ? res.data[0] : undefined;
            if (row?.status === FileStatus.ACTIVE) return resolve();
            if (row?.status === FileStatus.UPLOADING) {
              await AssetAttachmentsAPI.delete(asset_uuid, uuid, {
                skipErrorToast: true,
              }).catch(() => undefined);
            }
            reject(error);
          },
          () => reject(error)
        ),
    });
  });

const CreateValidationForm: FC<CreateValidationFormProps> = ({
  assetUuid,
  width,
  onSubmit,
  onCancel,
  onSubmittingChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();
  const { mutateAsync: createValidation } = useCreateAssetValidation();

  // The documents still to upload. After a partial failure only the failed
  // ones stay, so a retry re-sends exactly what is missing.
  const [files, setFiles] = useState<File[]>([]);
  // Set once POST /validations succeeds. From then on the form shows only
  // the documents (the row already exists; the flyout, ENG-1000, is where
  // it gets edited) and a submit skips the POST instead of creating a
  // second validation. The form's submit lock keeps two submits from
  // overlapping, so reading this from state is safe.
  const [created, setCreated] = useState<AssetValidation | null>(null);

  const uploadAll = async (pending: File[], validationUuid: string) => {
    const failed: { file: File; reason: string }[] = [];
    for (let i = 0; i < pending.length; i += UPLOAD_CONCURRENCY) {
      const chunk = pending.slice(i, i + UPLOAD_CONCURRENCY);
      const results = await Promise.allSettled(
        chunk.map((file) => uploadReport(file, assetUuid, validationUuid))
      );
      results.forEach((result, index) => {
        if (result.status === "rejected") {
          failed.push({
            file: chunk[index],
            reason:
              result.reason instanceof Error
                ? result.reason.message
                : String(result.reason),
          });
        }
      });
    }
    return failed;
  };

  const handleSubmit = async (payload: ValidationFormData) => {
    let validation = created;

    if (!validation) {
      try {
        validation = await createValidation(
          toCreateValidationBody(assetUuid, payload)
        );
        setCreated(validation);
      } catch (error) {
        // useCreateAssetValidation already opted out of the interceptor
        // toast and always throws with a message (the server's when it
        // sends one: a 400 "Unknown asset_uuid", a 403).
        console.error(error);
        enqueueSnackbar(
          error instanceof Error ? error.message : String(error),
          { variant: "error" }
        );
        return;
      }
    }

    const failed = await uploadAll(files, validation.uuid);

    if (failed.length === 0) {
      // The api writes the activity-log entries for the create and each
      // attachment; logging here too would duplicate them.
      enqueueSnackbar(
        `Validation ${
          validation.version ? `${validation.version} ` : ""
        }created`,
        { variant: "success" }
      );
      onSubmit?.();
      return;
    }

    console.error("Validation document uploads failed", failed);
    setFiles(failed.map((f) => f.file));
    const names = failed.map((f) => f.file.name).join(", ");
    enqueueSnackbar(
      `Validation saved, but ${failed.length} of ${files.length} document${
        files.length === 1 ? "" : "s"
      } failed to upload (${names}). Retry to upload just those.`,
      { variant: "error", autoHideDuration: 8000 }
    );
  };

  return (
    <FormWrapper width={width}>
      <DefaultValidationForm
        DefaultFormData={{ completed_by_id: currentUser?.user_id ?? null }}
        files={files}
        onFilesChange={setFiles}
        documentsOnly={!!created}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        onSubmittingChange={onSubmittingChange}
        buttonProps={{
          submitText: created ? "Retry Upload" : "Add Validation",
        }}
      />
    </FormWrapper>
  );
};

export default CreateValidationForm;
